import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import Button from '@mui/material/Button';
import WhatsNewPopover from 'ff/debrief/components/whatsNew/WhatsNewPopover';
import WhatsNewModal from 'ff/debrief/components/whatsNew/WhatsNewModal';
import { WhatsNewItem } from 'ff/debrief/models';
import {
    initialWhatsNewItems,
    WHATS_NEW_VERSIONS,
} from 'ff/debrief/components/whatsNew/DebriefWhatsNewItems';
import { hasDebriefLicense } from 'ff/debrief/utils';
import { useSyncSettings, useAnalytics } from 'ff/debrief/hooks';

const WhatsNewButton: React.FC = () => {
    const { savedSettings, saveTourSetting } = useSyncSettings();
    const { logWhatsNewItemViewed } = useAnalytics();

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [items, setItems] = useState<WhatsNewItem[]>(initialWhatsNewItems);
    const [modalIndex, setModalIndex] = useState<number | null>(null);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        setItems(
            initialWhatsNewItems.map(item => ({
                ...item,
                completed:
                    savedSettings?.savedTourSettings?.stepsStatus?.[item.id] ===
                    'completed',
            }))
        );
    }, [savedSettings]);

    const filteredItems = useMemo(() => {
        const now = new Date();
        return items.filter(item => {
            const cutoff = WHATS_NEW_VERSIONS[item.version];
            return !(cutoff && now > cutoff);
        });
    }, [items]);

    const numLeft = useMemo(
        () => filteredItems.filter(item => !item.completed).length,
        [filteredItems]
    );

    const handleOpenPopover = (event: React.MouseEvent<HTMLElement>) =>
        setAnchorEl(event.currentTarget);
    const handleClosePopover = () => setAnchorEl(null);

    const markItemAsCompleted = (itemId: string) => {
        const updatedItems = items.map(item =>
            item.id === itemId ? { ...item, completed: true } : item
        );
        setItems(updatedItems);

        const completedItems = updatedItems.filter(item => item.completed);
        const completedSteps: Record<string, 'completed'> = Object.fromEntries(
            completedItems.map(item => [item.id, 'completed'])
        );

        const settingsToSave = { stepsStatus: completedSteps };
        saveTourSetting(settingsToSave);
    };

    const handleRowClick = (id: string) => {
        const clickedItem = items.find(item => item.id === id);
        if (clickedItem) {
            logWhatsNewItemViewed({
                itemName: clickedItem.title,
                trigger: 'dropdown',
                wasUnread: !clickedItem.completed,
            });
        }
        markItemAsCompleted(id);
        setModalIndex(filteredItems.findIndex(item => item.id === id));
        setOpen(true);
    };

    const handleCloseModal = () => setOpen(false);
    const handleExited = () => setModalIndex(null);

    const handleNext = () => {
        setModalIndex(current => {
            if (current === null || current >= filteredItems.length - 1)
                return current;

            const next = current + 1;
            const nextItem = filteredItems[next];
            if (nextItem) {
                logWhatsNewItemViewed({
                    itemName: nextItem.title,
                    trigger: 'next_button',
                    wasUnread: !nextItem.completed,
                });
                markItemAsCompleted(nextItem.id);
            }
            return next;
        });
    };

    if (!hasDebriefLicense() || filteredItems.length === 0) return null;

    return (
        <>
            <StyledButton
                onClick={handleOpenPopover}
                endIcon={
                    savedSettings && numLeft > 0 ? (
                        <NumLeftIcon>{numLeft}</NumLeftIcon>
                    ) : null
                }
            >
                What&#39;s New
            </StyledButton>

            <WhatsNewPopover
                anchorEl={anchorEl}
                onClose={handleClosePopover}
                rows={filteredItems}
                onRowClick={handleRowClick}
            />

            <WhatsNewModal
                open={open}
                items={filteredItems}
                currentIndex={modalIndex ?? 0}
                onClose={handleCloseModal}
                onNext={handleNext}
                onExited={handleExited}
            />
        </>
    );
};

const NumLeftIcon = styled.span`
    width: 15px;
    height: 15px;
    background: #07c502;
    border-radius: 4px;
    display: flex;
    justify-content: center;
    align-items: center;
    color: white;
    font-size: 10px;
    font-weight: bold;
`;

const StyledButton = styled(Button)`
    background-color: #203040;
    color: white;
    padding: 8px 16px;
    border-radius: 4px;
    font-size: 0.8rem;
    font-weight: bold;

    .MuiButton-endIcon > *:nth-of-type(1) {
        font-size: 12px;
    }
`;

export default WhatsNewButton;
